import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";
import { formatMessage, type MessageValues } from "../../i18n/catalogUtils";

const en = {
  "evidence.eyebrow": "EVIDENCE CONFIDENTIALITY",
  "evidence.title": "Collector Evidence wrapping",
  "evidence.description": "Each collection generates a fresh AES-256 Evidence key on the endpoint and wraps it only to this server-owned public authority. No server decryption secret is distributed with a Portable ZIP or installer.",
  "loading": "Loading…",
  "refresh": "Refresh",
  "purpose": "Purpose",
  "evidence.rotate": "Generate rotation successor",
  "evidence.generate": "Generate staged authority",
  "evidence.safety": "Recent MFA protects generation and activation. Browser responses expose public identity only.",
  "evidence.rotationAria": "Evidence wrapping key rotation",
  "evidence.rotation": "Envelope-key rotation",
  "active": "ACTIVE",
  "inactive": "NOT ACTIVATED",
  "evidence.rotationHelp": "Generate → Stage → Activate NEW → new collections use NEW → OLD remains decrypt-only while encrypted dependencies exist. Destructive key removal is intentionally unavailable here until dependency-safe cryptographic-erasure checks pass.",
  "evidence.activeVersion": "Active v{version} · {fingerprint}",
  "evidence.authority": "Evidence wrapping authority",
  "evidence.activeCollections": "ACTIVE FOR NEW COLLECTIONS",
  "evidence.retained": "RETAINED · DECRYPT-ONLY",
  "staged": "STAGED",
  "keyVersion": "Key version",
  "notActive": "Not active",
  "fingerprint": "Public fingerprint",
  "pending": "Pending",
  "provider": "Provider",
  "privateMaterial": "Private material",
  "neverExposed": "Never exposed",
  "evidence.activeHelp": "Used only to wrap fresh per-collection DEKs. Private unwrap material remains in custody.",
  "evidence.retainedHelp": "Historical predecessor. Kept only to unwrap retained dependencies; routine reactivation is forbidden.",
  "evidence.stagedHelp": "Staged successor. It has never been active and can be activated for new collections.",
  "evidence.activate": "Activate for new collections",
  "evidence.empty": "No Evidence wrapping authority is staged yet.",
  "error.loadEvidence": "Evidence wrapping authority could not be loaded.",
  "error.stageEvidence": "Evidence wrapping identity could not be staged.",
  "error.activateEvidence": "Evidence wrapping identity could not be activated.",
  "evidence.pendingIdentity": "public identity pending",
  "evidence.stagedMessage": "Evidence wrapping successor staged: {identity}.",
  "evidence.activatedMessage": "Evidence wrapping key version {version} is active for new collections. Predecessor private keys remain decrypt-only for retained dependencies.",
  "signer.aria": "LAB Collector Release signer readiness",
  "signer.title": "LAB release signing readiness",
  "signer.ready": "Exactly one active Collector Release signer is available.",
  "signer.required": "LAB publication requires exactly one active Collector Release signer. Private key material stays in managed custody.",
  "signer.counts": "Active: {active} · Staged: {staged}",
  "signer.generate": "Generate signing identity",
  "signer.activate": "Activate signing identity (MFA required)",
  "signer.blocked": "Signer state needs administrator review; do not generate or activate another key.",
  "signer.refresh": "Refresh signing status",
  "signer.stagedMessage": "Signing identity staged in protected custody. Activate it before publishing.",
  "signer.activatedMessage": "Collector Release signer activated. Return to Publish to LAB Beta.",
  "error.readSigner": "Unable to read Collector Release signing status.",
  "error.stageSigner": "Unable to stage signing identity.",
  "error.activateSigner": "Unable to activate signing identity."
} as const;
type TrustAuthorityKey = keyof typeof en;
const fa: Record<TrustAuthorityKey, string> = {
  "evidence.eyebrow": "محرمانگی شواهد",
  "evidence.title": "محافظت از کلید شواهد کالکتور",
  "evidence.description": "در هر جمع‌آوری، کلید تازهٔ AES-256 روی دستگاه ساخته و با کلید عمومی تحت مدیریت سرور محافظت می‌شود. هیچ راز رمزگشایی سرور همراه بستهٔ قابل‌حمل یا نصب‌کننده توزیع نمی‌شود.",
  "loading": "در حال بارگذاری…",
  "refresh": "به‌روزرسانی",
  "purpose": "کاربرد",
  "evidence.rotate": "ساخت کلید جانشین",
  "evidence.generate": "ساخت کلید آمادهٔ فعال‌سازی",
  "evidence.safety": "ساخت و فعال‌سازی به تأیید چندمرحله‌ای اخیر نیاز دارد. مرورگر فقط مشخصات عمومی کلید را دریافت می‌کند.",
  "evidence.rotationAria": "تعویض کلید محافظت از شواهد",
  "evidence.rotation": "تعویض کلید محافظ",
  "active": "فعال",
  "inactive": "فعال نشده",
  "evidence.rotationHelp": "کلید جانشین ساخته و آماده می‌شود؛ پس از فعال‌سازی، جمع‌آوری‌های جدید از آن استفاده می‌کنند. کلید پیشین تا زمان وجود داده‌های وابسته فقط برای رمزگشایی نگه داشته می‌شود. حذف کلید تنها پس از بررسی ایمنی وابستگی‌ها امکان‌پذیر است.",
  "evidence.activeVersion": "نسخهٔ فعال {version} · {fingerprint}",
  "evidence.authority": "کلید محافظت از شواهد",
  "evidence.activeCollections": "فعال برای جمع‌آوری‌های جدید",
  "evidence.retained": "نگهداری‌شده · فقط رمزگشایی",
  "staged": "آمادهٔ فعال‌سازی",
  "keyVersion": "نسخهٔ کلید",
  "notActive": "غیرفعال",
  "fingerprint": "اثر انگشت عمومی",
  "pending": "در انتظار",
  "provider": "ارائه‌دهنده",
  "privateMaterial": "اطلاعات خصوصی کلید",
  "neverExposed": "هرگز نمایش داده نمی‌شود",
  "evidence.activeHelp": "فقط برای محافظت از کلید تازهٔ هر جمع‌آوری استفاده می‌شود. اطلاعات خصوصی رمزگشایی در محل امن باقی می‌ماند.",
  "evidence.retainedHelp": "کلید پیشین فقط برای رمزگشایی داده‌های وابسته نگه داشته می‌شود؛ فعال‌سازی دوبارهٔ معمول آن مجاز نیست.",
  "evidence.stagedHelp": "کلید جانشین آماده است؛ هنوز فعال نشده و می‌توان آن را برای جمع‌آوری‌های جدید فعال کرد.",
  "evidence.activate": "فعال‌سازی برای جمع‌آوری‌های جدید",
  "evidence.empty": "هنوز کلیدی برای محافظت از شواهد آماده نشده است.",
  "error.loadEvidence": "اطلاعات کلید محافظت از شواهد بارگذاری نشد.",
  "error.stageEvidence": "کلید محافظت از شواهد آماده نشد.",
  "error.activateEvidence": "کلید محافظت از شواهد فعال نشد.",
  "evidence.pendingIdentity": "مشخصات عمومی در انتظار آماده‌سازی",
  "evidence.stagedMessage": "کلید جانشین محافظت از شواهد آماده شد: {identity}.",
  "evidence.activatedMessage": "نسخهٔ {version} کلید محافظت از شواهد برای جمع‌آوری‌های جدید فعال شد. کلیدهای خصوصی پیشین فقط برای رمزگشایی داده‌های وابسته نگه داشته می‌شوند.",
  "signer.aria": "آمادگی امضای انتشار کالکتور در آزمایشگاه",
  "signer.title": "آمادگی امضای انتشار آزمایشگاهی",
  "signer.ready": "یک امضاکنندهٔ فعال برای انتشار کالکتور در دسترس است.",
  "signer.required": "انتشار آزمایشگاهی دقیقاً به یک امضاکنندهٔ فعال نیاز دارد. اطلاعات خصوصی کلید در محل امن مدیریت‌شده باقی می‌ماند.",
  "signer.counts": "فعال: {active} · آمادهٔ فعال‌سازی: {staged}",
  "signer.generate": "ساخت کلید امضا",
  "signer.activate": "فعال‌سازی کلید امضا (نیازمند تأیید چندمرحله‌ای)",
  "signer.blocked": "وضعیت امضاکننده به بررسی مدیر نیاز دارد؛ کلید دیگری نسازید و فعال نکنید.",
  "signer.refresh": "به‌روزرسانی وضعیت امضا",
  "signer.stagedMessage": "کلید امضا در محل امن آماده شد. پیش از انتشار آن را فعال کنید.",
  "signer.activatedMessage": "امضاکنندهٔ انتشار کالکتور فعال شد. به بخش انتشار آزمایشی در آزمایشگاه بازگردید.",
  "error.readSigner": "وضعیت امضای انتشار کالکتور دریافت نشد.",
  "error.stageSigner": "کلید امضا آماده نشد.",
  "error.activateSigner": "کلید امضا فعال نشد."
};

export function useTrustAuthorityCopy() {
  const { locale } = useWorkspaceCopy();
  return { t: (key: TrustAuthorityKey, values?: MessageValues) => formatMessage((locale === "fa" ? fa : en)[key], values) };
}
